
import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App";
import "./index.css";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Initialize Sentry as early as possible with comprehensive configuration
Sentry.init({
  dsn: "https://9418cbf39d99f1b835e8cc1d5b225215@o4509435253817344.ingest.de.sentry.io/4509435255783504",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
    // Console errors integration
    Sentry.extraErrorDataIntegration({
      depth: 10
    }),
    // Web Vitals for performance monitoring
    Sentry.browserProfilingIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.1, // 100% in dev, 10% in prod
  // Session Replay
  replaysSessionSampleRate: import.meta.env.DEV ? 1.0 : 0.1, // 100% in dev, 10% in prod
  replaysOnErrorSampleRate: 1.0, // Always capture replays on errors
  // Enhanced error tracking
  sendDefaultPii: true,
  environment: import.meta.env.DEV ? 'development' : 'production',
  // Additional configuration
  beforeSend(event, hint) {
    // Log errors to console in development
    if (import.meta.env.DEV) {
      console.error('Sentry Error:', event, hint);
    }
    return event;
  },
  // Set user context automatically
  initialScope: {
    tags: {
      component: "react-app"
    },
  },
});

// Create a client with error boundary
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Send query errors to Sentry
        Sentry.captureException(error, {
          tags: {
            errorType: 'react-query',
            failureCount
          }
        });
        return failureCount < 2;
      },
    },
    mutations: {
      onError: (error) => {
        // Send mutation errors to Sentry
        Sentry.captureException(error, {
          tags: {
            errorType: 'react-query-mutation'
          }
        });
      },
    },
  },
});

// Wrap the entire app with Sentry's ErrorBoundary
const SentryApp = Sentry.withErrorBoundary(App, {
  fallback: ({ error, resetError }) => (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md mx-auto text-center p-6 bg-white rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-red-600 mb-4">¡Oops! Algo salió mal</h2>
        <p className="text-gray-600 mb-4">
          Se ha producido un error inesperado. Nuestro equipo ha sido notificado automáticamente.
        </p>
        <button
          onClick={resetError}
          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded transition-colors"
        >
          Intentar de nuevo
        </button>
      </div>
    </div>
  ),
  beforeCapture: (scope, error, errorInfo) => {
    scope.setTag('errorBoundary', true);
    scope.setContext('errorInfo', { errorInfo });
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <SentryApp />
    </QueryClientProvider>
  </React.StrictMode>
);
